import * as React from 'react';
import styled from '@emotion/styled';
import { Spacings } from '@styles/index';
import Drawer from '@base/Drawer/Drawer';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMeUid } from '@api/selectors/auth';
import Avatar from '@base/Avatar/Avatar';
import Image from '@base/Image';
import Skeleton from '@material-ui/lab/Skeleton';
import { ILeaderboardUser } from 'types/groups/index';
import { getImage } from '@utils/imageHelper';
import { getAnimationPath } from '@utils/animationHelpers';
import LottieAnimation from '@base/LottieAnimation/LottieAnimation';
import H6 from '@base/H6/H6';
import H3 from '@base/H3/H3';
import H7 from '@base/H7/H7';
import P2 from '@base/P2/P2';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import {
  GROUP_LEADERBOARD_CLOSED,
  GROUP_WEEKLY_LEADERBOARD_CLICKED,
  PLANNER_PAGE
} from '@constants/segment';
import { getCurrentWeekString } from '../groupsUtils';
import GroupsScoreboardCard from '../CommonCards/GroupsScoreboardCard';
import { leaderboardRankSize } from '../groupsConstants';

interface IGroupsLeaderboardCardProps {
  leaderboard: ILeaderboardUser[];
  startDate: string;
  endDate: string;
  goalUID: string;
  meUserRank: number;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: ${Spacings.SPACING_5B};
  padding-top: ${Spacings.SPACING_4B};
  padding-bottom: ${Spacings.SPACING_4B};
  border-radius: ${Spacings.SPACING_2B};
  background-image: url('${getImage('groups/leaderboard-frame.png')}');
  background-size: contain;
  background-repeat: no-repeat;
`;

const AvatarWrapper = styled.div`
  position: relative;
`;

const AvatarRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 9px;
  margin-bottom: ${Spacings.SPACING_3B};
`;

const StyP2 = styled(P2)`
  margin-top: ${Spacings.SPACING_6};
  color: var(--color-i-white);
  max-width: 214px;
  text-align: center;
  line-height: ${Spacings.SPACING_4B};
`;

const StyleP2 = styled(P2)`
  margin-top: ${Spacings.SPACING_6};
  color: var(--color-text-secondary);
  max-width: 271px;
`;

const SeeWinnersWrapper = styled.div`
  display: inline-flex;
  cursor: pointer;
  margin-top: ${Spacings.SPACING_3B};
  padding-bottom: 7px;
  border-bottom: ${Spacings.SPACING_1} dashed var(--color-i-white);
`;

interface IImageWrapperProps {
  height: string;
  width: string;
}
const ImageWrapper = styled.div<IImageWrapperProps>`
  display: flex;
  position: absolute;
  bottom: 0;
  height: ${({ height }) => height};
  width: ${({ width }) => width};
`;

const HeadingWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: ${Spacings.SPACING_11B};
`;

interface IAnimationContainerProps {
  display: string;
}

const AnimationContainer = styled(LottieAnimation)<IAnimationContainerProps>`
  height: ${Spacings.SPACING_20B};
  width: ${Spacings.SPACING_20B};
  display: ${(props) => props.display};
`;

const SkeletonWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DrawerWrapper = styled.div`
  grid-column: 1/10;
  padding-bottom: ${Spacings.SPACING_6B};
`;

const StyH7 = styled(H7)`
  line-height: ${Spacings.SPACING_4B};
`;

const StyH6 = styled(H6)`
  line-height: 18px;
`;

const numberArray = ['one', 'two', 'three'];

const GroupsLeaderboardCard: React.FC<IGroupsLeaderboardCardProps> = ({
  leaderboard = [],
  startDate,
  endDate,
  goalUID,
  meUserRank
}) => {
  const topThreeWinners =
    leaderboard.length >= 3
      ? [leaderboard[1], leaderboard[0], leaderboard[2]]
      : leaderboard;
  let topThreeWinnersAvatars = [];
  if (topThreeWinners.length >= 3) {
    topThreeWinnersAvatars = [leaderboard[1], leaderboard[0], leaderboard[2]];
  } else if (topThreeWinners.length === 2) {
    topThreeWinnersAvatars = [leaderboard[1], leaderboard[0]];
  } else {
    topThreeWinnersAvatars = leaderboard;
  }

  const currentUserId = useMyInfo(getMeUid);
  const currentUser = leaderboard.find((user) => user.uid === currentUserId);
  const goalSegmentData = useGoalEventProps({ goalUID });

  const [drawerState, setDrawerState] = React.useState(false);
  const [isAnimationLoop, setIsAnimationLoop] = React.useState(false);

  const openDrawerHandler = () => {
    setDrawerState(true);
    segment.track(GROUP_WEEKLY_LEADERBOARD_CLICKED, {
      ...goalSegmentData,
      last_primary_source: PLANNER_PAGE,
      last_primary_source_section: 'Planner',
      group_size: leaderboard.length
    });
  };
  const closeDrawerHandler = () => {
    setDrawerState(false);
    segment.track(GROUP_LEADERBOARD_CLOSED, {
      ...goalSegmentData,
      last_primary_source: PLANNER_PAGE,
      last_primary_source_section: 'Planner',
      group_size: leaderboard.length
    });
  };

  return (
    <Wrapper onClick={(e) => e.stopPropagation()}>
      <AvatarRow data-testid="groups-leaderboardcard-avatar">
        {topThreeWinners.map((user, i) => (
          <AvatarWrapper
            key={user.uid}
            data-testid={`groups-leaderboardcard-avatar-${
              numberArray[user.position - 1]
            }`}
          >
            <Avatar
              src={user.avatar}
              circular
              size={user.position === 1 ? '42px' : Spacings.SPACING_9B}
              alt="leaderboard"
            />
            <ImageWrapper
              height={leaderboardRankSize[i].height}
              width={leaderboardRankSize[i].width}
            >
              <Image
                src={`groups/leaderboard-card-${
                  numberArray[user.position - 1]
                }.png`}
                height={leaderboardRankSize[i].height}
                width={leaderboardRankSize[i].width}
                alt="leaderboard-rank"
              />
            </ImageWrapper>
          </AvatarWrapper>
        ))}
      </AvatarRow>
      <StyH6 color="var(--color-i-white)">Leaderboard announced</StyH6>
      <StyP2 data-testid="groups-leaderboardcard-rank">
        You ranked #{meUserRank} in {getCurrentWeekString(startDate, endDate)}{' '}
        weekly competition.
      </StyP2>
      <SeeWinnersWrapper onClick={openDrawerHandler}>
        <StyH7 color="var(--color-i-white)">See all winners</StyH7>
      </SeeWinnersWrapper>
      <Drawer
        drawerState={drawerState}
        onClose={closeDrawerHandler}
        backgroundColor="var(--color-base-1)"
      >
        <DrawerWrapper>
          <HeadingWrapper>
            <div>
              <H3>{getCurrentWeekString(startDate, endDate)} winners</H3>
              <StyleP2>
                Get points for every minute you watch, every question you
                practice. At the end of a week, the member with the highest
                points wins!
              </StyleP2>
            </div>
            <AnimationContainer
              src={getAnimationPath('leaderboard.json')}
              loop={false}
              display={isAnimationLoop ? 'none' : 'block'}
              events={{ complete: () => setIsAnimationLoop(true) }}
              Skeleton={
                <SkeletonWrapper>
                  <Skeleton
                    variant="circle"
                    width={Spacings.SPACING_5B}
                    height={Spacings.SPACING_5B}
                  />
                </SkeletonWrapper>
              }
            />
            <AnimationContainer
              src={getAnimationPath('leaderboardLoop.json')}
              display={isAnimationLoop ? 'block' : 'none'}
              Skeleton={
                <SkeletonWrapper>
                  <Skeleton
                    variant="circle"
                    width={Spacings.SPACING_5B}
                    height={Spacings.SPACING_5B}
                  />
                </SkeletonWrapper>
              }
            />
          </HeadingWrapper>
          <GroupsScoreboardCard type="drawer" showAvatar showTrophy />
        </DrawerWrapper>
      </Drawer>
    </Wrapper>
  );
};

export default GroupsLeaderboardCard;
